import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  changePasswordRequest,
  listSessionsRequest,
  logoutAllRequest,
  resolveApiErrorMessage,
  revokeSessionRequest,
} from '../services/authApi';

export default function ProfilePage() {
  const { user, accessToken, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('account');
  const [sessions, setSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [sessionsMessage, setSessionsMessage] = useState('');
  const [sessionsError, setSessionsError] = useState('');
  const [securityMessage, setSecurityMessage] = useState('');
  const [isLoggingOutAll, setIsLoggingOutAll] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [sessionFilters, setSessionFilters] = useState({
    search: '',
    status: 'all',
    rememberMe: 'all',
    current: 'all',
    initialIp: '',
    lastIp: '',
    userAgent: '',
    revokeReason: '',
    createdAt: '',
    lastActivity: '',
  });
  const [sessionsPage, setSessionsPage] = useState(1);
  const [sessionsPerPage, setSessionsPerPage] = useState(10);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [passwordFormMessage, setPasswordFormMessage] = useState('');
  const [passwordFormError, setPasswordFormError] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);

  const userDisplayName = useMemo(
    () => user?.full_name || user?.username || 'User Profile',
    [user?.full_name, user?.username]
  );

  const loadSessions = useCallback(async () => {
    if (!accessToken) {
      setSessions([]);
      return;
    }

    setIsLoadingSessions(true);
    setSessionsError('');
    setSessionsMessage('');

    try {
      const response = await listSessionsRequest(accessToken);
      const source =
        response?.data?.sessions ||
        response?.data ||
        response?.sessions ||
        [];

      setSessions(Array.isArray(source) ? source : []);
      if (!Array.isArray(source) || source.length === 0) {
        setSessionsMessage('No active sessions found.');
      }
    } catch (error) {
      setSessions([]);
      setSessionsError(
        resolveApiErrorMessage(error, 'Failed to load active sessions.')
      );
    } finally {
      setIsLoadingSessions(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (activeTab === 'sessions') {
      loadSessions();
    }
  }, [activeTab, loadSessions]);

  useEffect(() => {
    setSessionsPage(1);
  }, [
    sessionFilters.search,
    sessionFilters.status,
    sessionFilters.rememberMe,
    sessionFilters.current,
    sessionFilters.initialIp,
    sessionFilters.lastIp,
    sessionFilters.userAgent,
    sessionFilters.revokeReason,
    sessionFilters.createdAt,
    sessionFilters.lastActivity,
    sessionsPerPage,
  ]);

  function isCurrentSession(session) {
    return Boolean(
      session?.is_current ||
      session?.is_current_session ||
      session?.current ||
      session?.isCurrent
    );
  }

  async function handleRevokeSession(sessionId, currentSessionFlag) {
    if (!accessToken || !sessionId) {
      return;
    }

    if (currentSessionFlag) {
      setSessionsError('Current active session cannot be revoked from this action. Use Logout All Sessions if needed.');
      return;
    }

    setSessionsError('');
    setSessionsMessage('');

    try {
      await revokeSessionRequest(accessToken, sessionId);
      setSessionsMessage('Session revoked successfully.');
      await loadSessions();
    } catch (error) {
      setSessionsError(
        resolveApiErrorMessage(error, 'Failed to revoke session.')
      );
    }
  }

  function handlePasswordInputChange(event) {
    const { name, value } = event.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  async function handleChangePasswordSubmit(event) {
    event.preventDefault();
    setPasswordFormError('');
    setPasswordFormMessage('');

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmNewPassword) {
      setPasswordFormError('Please complete all password fields.');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setPasswordFormError('New password must be at least 8 characters.');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmNewPassword) {
      setPasswordFormError('New password and confirmation do not match.');
      return;
    }

    if (!accessToken) {
      setPasswordFormError('Missing access token. Please login again.');
      return;
    }

    setIsChangingPassword(true);

    try {
      await changePasswordRequest(accessToken, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
        new_password_confirmation: passwordForm.confirmNewPassword,
      });

      setPasswordFormMessage('Password changed successfully. Other active sessions were revoked.');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
      });
      await loadSessions();
    } catch (error) {
      setPasswordFormError(
        resolveApiErrorMessage(error, 'Failed to change password.')
      );
    } finally {
      setIsChangingPassword(false);
    }
  }

  function handleSessionFilterChange(event) {
    const { name, value } = event.target;
    setSessionFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  const filteredSessions = useMemo(() => {
    return sessions.filter((session) => {
      const sessionId = String(session?.id || session?.session_id || '').toLowerCase();
      const status = String(session?.status || '').toLowerCase();
      const rememberMe = Number(session?.remember_me || 0) === 1;
      const currentSession = isCurrentSession(session);
      const initialIp = String(session?.initial_ip_address || '').toLowerCase();
      const lastIp = String(session?.last_ip_address || '').toLowerCase();
      const userAgent = String(session?.user_agent || '').toLowerCase();
      const revokeReason = String(session?.revoke_reason || '').toLowerCase();
      const createdAt = String(session?.created_at || '').toLowerCase();
      const lastActivity = String(session?.last_activity_at || session?.last_refresh_at || '').toLowerCase();

      const searchFilter = sessionFilters.search.trim().toLowerCase();
      if (searchFilter !== '' && !sessionId.includes(searchFilter)) {
        return false;
      }

      if (sessionFilters.status !== 'all' && status !== sessionFilters.status) {
        return false;
      }

      if (sessionFilters.rememberMe === 'yes' && !rememberMe) {
        return false;
      }

      if (sessionFilters.rememberMe === 'no' && rememberMe) {
        return false;
      }

      if (sessionFilters.current === 'yes' && !currentSession) {
        return false;
      }

      if (sessionFilters.current === 'no' && currentSession) {
        return false;
      }

      if (sessionFilters.initialIp.trim() !== '' && !initialIp.includes(sessionFilters.initialIp.trim().toLowerCase())) {
        return false;
      }

      if (sessionFilters.lastIp.trim() !== '' && !lastIp.includes(sessionFilters.lastIp.trim().toLowerCase())) {
        return false;
      }

      if (sessionFilters.userAgent.trim() !== '' && !userAgent.includes(sessionFilters.userAgent.trim().toLowerCase())) {
        return false;
      }

      if (sessionFilters.revokeReason.trim() !== '' && !revokeReason.includes(sessionFilters.revokeReason.trim().toLowerCase())) {
        return false;
      }

      if (sessionFilters.createdAt.trim() !== '' && !createdAt.includes(sessionFilters.createdAt.trim().toLowerCase())) {
        return false;
      }

      if (sessionFilters.lastActivity.trim() !== '' && !lastActivity.includes(sessionFilters.lastActivity.trim().toLowerCase())) {
        return false;
      }

      return true;
    });
  }, [sessions, sessionFilters]);

  const totalSessionPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredSessions.length / sessionsPerPage));
  }, [filteredSessions.length, sessionsPerPage]);

  useEffect(() => {
    if (sessionsPage > totalSessionPages) {
      setSessionsPage(totalSessionPages);
    }
  }, [sessionsPage, totalSessionPages]);

  const pagedSessions = useMemo(() => {
    const start = (sessionsPage - 1) * sessionsPerPage;
    return filteredSessions.slice(start, start + sessionsPerPage);
  }, [filteredSessions, sessionsPage, sessionsPerPage]);

  const pageNumberWindow = useMemo(() => {
    const windowSize = 5;
    const start = Math.max(1, sessionsPage - 2);
    const end = Math.min(totalSessionPages, start + windowSize - 1);
    const normalizedStart = Math.max(1, end - windowSize + 1);
    const pages = [];

    for (let page = normalizedStart; page <= end; page += 1) {
      pages.push(page);
    }

    return pages;
  }, [sessionsPage, totalSessionPages]);

  async function handleLogoutAllSessions() {
    if (!accessToken) {
      return;
    }

    setIsLoggingOutAll(true);
    setSecurityMessage('');

    try {
      await logoutAllRequest(accessToken);
      await logout();
    } catch (error) {
      setSecurityMessage(
        resolveApiErrorMessage(error, 'Failed to logout all sessions.')
      );
    } finally {
      setIsLoggingOutAll(false);
    }
  }

  function openSessionDetails(session) {
    if (!session) {
      return;
    }

    setSelectedSession(session);
    setIsSessionModalOpen(true);
  }

  function closeSessionDetails() {
    setIsSessionModalOpen(false);
    setSelectedSession(null);
  }

  function truncateValue(value, maxLength = 28) {
    const normalized = String(value || '-');
    if (normalized.length <= maxLength) {
      return normalized;
    }

    return `${normalized.slice(0, Math.max(0, maxLength - 3))}...`;
  }

  function renderAccountTab() {
    return (
      <section className="profile-tab-panel">
        <div className="profile-info-grid">
          <article className="profile-info-card">
            <h3>Account Details</h3>
            <div className="profile-kv-list">
              <p><strong>Username</strong><span>{user?.username || '-'}</span></p>
              <p><strong>Email</strong><span>{user?.email || '-'}</span></p>
              <p><strong>Full Name</strong><span>{user?.full_name || '-'}</span></p>
              <p><strong>Status</strong><span className="profile-status-badge">{user?.status || 'active'}</span></p>
            </div>
          </article>

          <article className="profile-info-card">
            <h3>Profile Summary</h3>
            <p>This module is aligned with your current system shell and now supports tabbed profile management.</p>
            <p>Security and session controls are connected to your existing OAuth routes.</p>
          </article>
        </div>
      </section>
    );
  }

  function renderSecurityTab() {
    return (
      <section className="profile-tab-panel">
        <div className="profile-info-grid">
          <article className="profile-info-card">
            <h3>Change Password</h3>

            <form className="profile-form" onSubmit={handleChangePasswordSubmit}>
              <label htmlFor="currentPassword">Current Password</label>
              <input
                id="currentPassword"
                name="currentPassword"
                type="password"
                value={passwordForm.currentPassword}
                onChange={handlePasswordInputChange}
                autoComplete="current-password"
              />

              <label htmlFor="newPassword">New Password</label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                value={passwordForm.newPassword}
                onChange={handlePasswordInputChange}
                autoComplete="new-password"
              />

              <label htmlFor="confirmNewPassword">Confirm New Password</label>
              <input
                id="confirmNewPassword"
                name="confirmNewPassword"
                type="password"
                value={passwordForm.confirmNewPassword}
                onChange={handlePasswordInputChange}
                autoComplete="new-password"
              />

              {passwordFormError ? <div className="error-box">{passwordFormError}</div> : null}
              {passwordFormMessage ? <div className="success-box">{passwordFormMessage}</div> : null}

              <div className="profile-actions-row">
                <button type="submit" className="profile-secondary-btn" disabled={isChangingPassword}>
                  {isChangingPassword ? 'Changing Password...' : 'Change Password'}
                </button>
              </div>
            </form>
          </article>

          <article className="profile-info-card">
            <h3>Session Security</h3>
            <p>Use this to end all active sessions for your account immediately.</p>

            {securityMessage ? <div className="error-box">{securityMessage}</div> : null}

            <div className="profile-actions-row">
              <button
                type="button"
                className="profile-danger-btn"
                onClick={handleLogoutAllSessions}
                disabled={isLoggingOutAll}
              >
                {isLoggingOutAll ? 'Processing...' : 'Logout All Sessions'}
              </button>
            </div>
          </article>
        </div>
      </section>
    );
  }

  function renderSessionsTab() {
    const renderSessionSkeletonRows = (rowCount = 8) => {
      return Array.from({ length: rowCount }).map((_, rowIndex) => (
        <tr key={`session-skeleton-${rowIndex}`}>
          {Array.from({ length: 17 }).map((__, colIndex) => (
            <td key={`session-skeleton-${rowIndex}-${colIndex}`}>
              <span className="profile-skeleton-cell" />
            </td>
          ))}
        </tr>
      ));
    };

    return (
      <section className="profile-tab-panel">
        <div className="profile-toolbar-row">
          <div className="profile-filters-grid">
            <input
              type="text"
              name="search"
              value={sessionFilters.search}
              onChange={handleSessionFilterChange}
              placeholder="Search Session ID"
            />
            <select name="status" value={sessionFilters.status} onChange={handleSessionFilterChange}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="revoked">Revoked</option>
              <option value="expired">Expired</option>
              <option value="compromised">Compromised</option>
            </select>
            <select name="rememberMe" value={sessionFilters.rememberMe} onChange={handleSessionFilterChange}>
              <option value="all">Remember Me: All</option>
              <option value="yes">Remember Me: Yes</option>
              <option value="no">Remember Me: No</option>
            </select>
            <select name="current" value={sessionFilters.current} onChange={handleSessionFilterChange}>
              <option value="all">Current: All</option>
              <option value="yes">Current: Yes</option>
              <option value="no">Current: No</option>
            </select>
            <input type="text" name="initialIp" value={sessionFilters.initialIp} onChange={handleSessionFilterChange} placeholder="Filter Initial IP" />
            <input type="text" name="lastIp" value={sessionFilters.lastIp} onChange={handleSessionFilterChange} placeholder="Filter Last IP" />
            <input type="text" name="userAgent" value={sessionFilters.userAgent} onChange={handleSessionFilterChange} placeholder="Filter User Agent" />
            <input type="text" name="revokeReason" value={sessionFilters.revokeReason} onChange={handleSessionFilterChange} placeholder="Filter Revoke Reason" />
            <input type="text" name="createdAt" value={sessionFilters.createdAt} onChange={handleSessionFilterChange} placeholder="Filter Created At" />
            <input type="text" name="lastActivity" value={sessionFilters.lastActivity} onChange={handleSessionFilterChange} placeholder="Filter Last Activity" />
          </div>

          <div className="profile-actions-row">
            <select
              className="profile-per-page"
              value={sessionsPerPage}
              onChange={(event) => setSessionsPerPage(Number(event.target.value))}
            >
              <option value={5}>5 / page</option>
              <option value={10}>10 / page</option>
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
            </select>
            <button type="button" className="profile-secondary-btn" onClick={loadSessions}>
              Refresh Sessions
            </button>
          </div>
        </div>

        {sessionsError ? <div className="error-box">{sessionsError}</div> : null}
        {sessionsMessage ? <div className="success-box">{sessionsMessage}</div> : null}

        {isLoadingSessions ? (
          <div className="profile-table-wrap">
            <table className="profile-sessions-table profile-sessions-table-skeleton">
              <thead>
                <tr>
                  <th>Actions</th>
                  <th>Current</th>
                  <th>Session ID</th>
                  <th>Status</th>
                  <th>Remember Me</th>
                  <th>Initial IP</th>
                  <th>Last IP</th>
                  <th>IP Changes</th>
                  <th>User Agent</th>
                  <th>Last Activity</th>
                  <th>Last Refresh</th>
                  <th>Idle Expires</th>
                  <th>Absolute Expires</th>
                  <th>Revoked At</th>
                  <th>Revoke Reason</th>
                  <th>Created At</th>
                  <th>Updated At</th>
                </tr>
              </thead>
              <tbody>
                {renderSessionSkeletonRows()}
              </tbody>
            </table>
          </div>
        ) : (
          <>
            <div className="profile-table-wrap">
              <table className="profile-sessions-table">
                <thead>
                  <tr>
                    <th>Actions</th>
                    <th>Current</th>
                    <th>Session ID</th>
                    <th>Status</th>
                    <th>Remember Me</th>
                    <th>Initial IP</th>
                    <th>Last IP</th>
                    <th>IP Changes</th>
                    <th>User Agent</th>
                    <th>Last Activity</th>
                    <th>Last Refresh</th>
                    <th>Idle Expires</th>
                    <th>Absolute Expires</th>
                    <th>Revoked At</th>
                    <th>Revoke Reason</th>
                    <th>Created At</th>
                    <th>Updated At</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedSessions.map((session, index) => {
                    const sessionId = session?.id || session?.session_id || '';
                    const status = session?.status || 'unknown';
                    const currentSession = isCurrentSession(session);
                    const userAgent = session?.user_agent || '-';

                    return (
                      <tr key={sessionId || `session-${index}`}>
                        <td>
                          <div className="profile-session-actions">
                            <button
                              type="button"
                              className="profile-secondary-btn profile-secondary-btn-xs"
                              onClick={() => openSessionDetails(session)}
                            >
                              View
                            </button>
                            <button
                              type="button"
                              className="profile-danger-btn profile-danger-btn-xs"
                              onClick={() => handleRevokeSession(sessionId, currentSession)}
                              disabled={!sessionId || status !== 'active' || currentSession}
                            >
                              {currentSession ? 'Current' : 'Revoke'}
                            </button>
                          </div>
                        </td>
                        <td>
                          {currentSession ? <span className="profile-current-badge">Current</span> : <span>-</span>}
                        </td>
                        <td className="profile-ellipsis-cell profile-session-id-cell" title={sessionId || '-'}>{truncateValue(sessionId || '-', 24)}</td>
                        <td>{status}</td>
                        <td>{Number(session?.remember_me || 0) === 1 ? 'Yes' : 'No'}</td>
                        <td>{session?.initial_ip_address || '-'}</td>
                        <td>{session?.last_ip_address || '-'}</td>
                        <td>{session?.ip_change_count ?? 0}</td>
                        <td className="profile-ellipsis-cell profile-ua-cell" title={userAgent}>{truncateValue(userAgent, 56)}</td>
                        <td>{session?.last_activity_at || '-'}</td>
                        <td>{session?.last_refresh_at || '-'}</td>
                        <td>{session?.idle_expires_at || '-'}</td>
                        <td>{session?.absolute_expires_at || '-'}</td>
                        <td>{session?.revoked_at || '-'}</td>
                        <td>{session?.revoke_reason || '-'}</td>
                        <td>{session?.created_at || '-'}</td>
                        <td>{session?.updated_at || '-'}</td>
                      </tr>
                    );
                  })}

                  {!pagedSessions.length ? (
                    <tr>
                      <td colSpan={17} className="profile-empty-row">No sessions found for current filters.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            <div className="profile-pagination-row">
              <button type="button" className="profile-page-btn" onClick={() => setSessionsPage(1)} disabled={sessionsPage === 1}>First</button>
              <button type="button" className="profile-page-btn" onClick={() => setSessionsPage((prev) => Math.max(1, prev - 1))} disabled={sessionsPage === 1}>Previous</button>

              {pageNumberWindow.map((pageNumber) => (
                <button
                  type="button"
                  key={pageNumber}
                  className={`profile-page-btn ${sessionsPage === pageNumber ? 'is-active' : ''}`}
                  onClick={() => setSessionsPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              ))}

              <button
                type="button"
                className="profile-page-btn"
                onClick={() => setSessionsPage((prev) => Math.min(totalSessionPages, prev + 1))}
                disabled={sessionsPage >= totalSessionPages}
              >
                Next
              </button>
              <button
                type="button"
                className="profile-page-btn"
                onClick={() => setSessionsPage(totalSessionPages)}
                disabled={sessionsPage >= totalSessionPages}
              >
                Last
              </button>
            </div>

            {isSessionModalOpen && selectedSession ? (
              <div
                className="profile-session-modal-backdrop"
                role="presentation"
              >
                <div
                  className="profile-session-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Session details"
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="profile-session-modal-header">
                    <h3>Session Details</h3>
                    <button
                      type="button"
                      className="profile-secondary-btn profile-secondary-btn-xs"
                      onClick={closeSessionDetails}
                    >
                      Close
                    </button>
                  </div>

                  <div className="profile-session-modal-body">
                    <div className="profile-session-modal-grid">
                      <p><strong>Session ID</strong><span>{selectedSession?.id || selectedSession?.session_id || '-'}</span></p>
                      <p><strong>Status</strong><span>{selectedSession?.status || '-'}</span></p>
                      <p><strong>Current Session</strong><span>{isCurrentSession(selectedSession) ? 'Yes' : 'No'}</span></p>
                      <p><strong>Remember Me</strong><span>{Number(selectedSession?.remember_me || 0) === 1 ? 'Yes' : 'No'}</span></p>
                      <p><strong>Initial IP</strong><span>{selectedSession?.initial_ip_address || '-'}</span></p>
                      <p><strong>Last IP</strong><span>{selectedSession?.last_ip_address || '-'}</span></p>
                      <p><strong>IP Change Count</strong><span>{selectedSession?.ip_change_count ?? 0}</span></p>
                      <p><strong>Last Activity</strong><span>{selectedSession?.last_activity_at || '-'}</span></p>
                      <p><strong>Last Refresh</strong><span>{selectedSession?.last_refresh_at || '-'}</span></p>
                      <p><strong>Idle Expires</strong><span>{selectedSession?.idle_expires_at || '-'}</span></p>
                      <p><strong>Absolute Expires</strong><span>{selectedSession?.absolute_expires_at || '-'}</span></p>
                      <p><strong>Revoked At</strong><span>{selectedSession?.revoked_at || '-'}</span></p>
                      <p><strong>Revoke Reason</strong><span>{selectedSession?.revoke_reason || '-'}</span></p>
                      <p><strong>Created At</strong><span>{selectedSession?.created_at || '-'}</span></p>
                      <p><strong>Updated At</strong><span>{selectedSession?.updated_at || '-'}</span></p>
                      <p className="profile-session-modal-row-full"><strong>User Agent</strong><span>{selectedSession?.user_agent || '-'}</span></p>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </>
        )}
      </section>
    );
  }

  return (
    <div className="module-screen">
      <header className="dashboard-header">
        <div>
          <p className="eyebrow">User Profile</p>
          <h1>{userDisplayName}</h1>
          <span className="subtle">Manage account details, password workflow, and OAuth sessions</span>
        </div>
      </header>

      <section className="profile-tabs-shell">
        <div className="profile-tabs-row">
          <button
            type="button"
            className={`profile-tab-btn ${activeTab === 'account' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('account')}
          >
            Account
          </button>
          <button
            type="button"
            className={`profile-tab-btn ${activeTab === 'security' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            Security
          </button>
          <button
            type="button"
            className={`profile-tab-btn ${activeTab === 'sessions' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('sessions')}
          >
            Sessions
          </button>
        </div>

        {activeTab === 'account' ? renderAccountTab() : null}
        {activeTab === 'security' ? renderSecurityTab() : null}
        {activeTab === 'sessions' ? renderSessionsTab() : null}
      </section>
    </div>
  );
}
